import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { CategoryVat, OcSettings, OcSettingsUpdate } from "../types";
import { queryKeys } from "./queryKeys";

/** The company's OC settings (`GET`, MANAGE_PURCHASE_ORDER_APPROVALS + OC module). */
export const useOcSettings = () =>
  useQuery<OcSettings>({
    queryKey: [queryKeys.PURCHASE_ORDER_SETTINGS],
    queryFn: async () => {
      const { data } = await axios.get("/purchase-orders/settings");
      return data;
    },
  });

/**
 * `PUT` is PARTIAL: a field that is null / omitted is left unchanged server-side, so callers send
 * only what the user actually changed.
 */
export const useUpdateOcSettings = () => {
  const queryClient = useQueryClient();
  return useMutation<OcSettings, Error, OcSettingsUpdate>({
    mutationFn: async (changes) => {
      const { data } = await axios.put("/purchase-orders/settings", changes);
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_ORDER_SETTINGS],
      }),
  });
};

/** The company's per-category VAT overrides. */
export const useCategoryVat = () =>
  useQuery<CategoryVat[]>({
    queryKey: [queryKeys.PURCHASE_ORDER_CATEGORY_VAT],
    queryFn: async () => {
      const { data } = await axios.get(
        "/purchase-orders/settings/category-vat"
      );
      return data;
    },
  });

/**
 * `PUT` REPLACES the whole set of per-category overrides (anything omitted is deleted server-side,
 * `[]` clears them all). A 400 means an unknown / duplicate category or a rate outside 0-100.
 */
export const useReplaceCategoryVat = () => {
  const queryClient = useQueryClient();
  return useMutation<CategoryVat[], Error, CategoryVat[]>({
    mutationFn: async (rows) => {
      const { data } = await axios.put(
        "/purchase-orders/settings/category-vat",
        rows
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_ORDER_CATEGORY_VAT],
      }),
  });
};
